/**
 * Image optimization utilities for performance
 * Handles lazy loading, preloading, and WebP support
 */
import { productionLogger } from '@/utils/production-logger';

// Check WebP support
export const supportsWebP = (() => {
  if (typeof window === 'undefined') return false;
  
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  
  return canvas.toDataURL('image/webp').startsWith('data:image/webp');
})();

// Generate optimized image URL with size parameters
export const getOptimizedImageUrl = (
  originalUrl: string, 
  width?: number, 
  height?: number,
  format?: 'webp' | 'jpg' | 'png'
) => {
  if (!originalUrl) return originalUrl;
  
  // For Shopify images, add size parameters
  if (originalUrl.includes('.shopify.com') || originalUrl.includes('cdn.shopify.com')) {
    const url = new URL(originalUrl);
    const params = new URLSearchParams();
    
    if (width) params.set('width', width.toString());
    if (height) params.set('height', height.toString());
    if (format && format !== 'jpg') params.set('format', format);
    
    if (params.toString()) {
      url.search = params.toString();
    }
    
    return url.toString();
  }
  
  return originalUrl;
};

// Generate srcSet for responsive images
export const generateSrcSet = (originalUrl: string, sizes: number[]) => {
  return sizes
    .map(size => `${getOptimizedImageUrl(originalUrl, size)} ${size}w`)
    .join(', ');
};

// Preload critical images
export const preloadImage = (src: string, priority: boolean = false) => {
  if (typeof window === 'undefined') return;
  
  const link = document.createElement('link');
  link.rel = priority ? 'preload' : 'prefetch';
  link.as = 'image';
  link.href = src;
  
  // Only set crossorigin for external domains, never for relative URLs or same-origin
  const shouldUseCrossorigin = src.startsWith('http') && 
      !src.startsWith(window.location.origin) &&
      !src.startsWith('/');
      
  productionLogger.debug('Preloading image:', src, 'crossorigin:', shouldUseCrossorigin);
  
  if (shouldUseCrossorigin) {
    link.setAttribute('crossorigin', 'anonymous');
  }
  
  if (supportsWebP && !src.includes('format=')) {
    link.href = getOptimizedImageUrl(src, undefined, undefined, 'webp');
  }
  
  document.head.appendChild(link);
};

// Create intersection observer for lazy loading
export const createLazyLoadObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options: IntersectionObserverInit = {}
) => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return null;
  }
  
  return new IntersectionObserver(callback, {
    rootMargin: '50px',
    threshold: 0.1,
    ...options
  });
};

// Batch preload multiple images
export const preloadImages = (urls: string[], priority: boolean = false) => {
  urls.forEach(url => preloadImage(url, priority));
};

// Generate placeholder data URL
export const generatePlaceholder = (width: number = 400, height: number = 300, color: string = '#f3f4f6') => {
  return `data:image/svg+xml;base64,${btoa(
    `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="${color}"/>
      <text x="50%" y="50%" font-family="Arial" font-size="14" fill="#9ca3af" text-anchor="middle" dy=".3em">Loading...</text>
    </svg>`
  )}`;
};